import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { adminAPI } from '../services/api';

interface User {
  id: number;
  name: string;
  email: string;
  phone: string;
  business_name: string;
  is_active: boolean;
  created_at: string;
  last_login: string;
  wallet_balance: number;
  account_balance: number;
  total_payouts: number;
  total_transactions: number;
  role_name: string;
  client_id?: string;
  api_key?: string;
  client_secret?: string;
  merchants_created?: number;
  viyona_enabled?: number | boolean;
  poolpay_enabled?: number | boolean;
  add_money_enabled?: number | boolean;
  admin_controls_payout_options?: number | boolean;
  unauthorised_ip_detected?: number | boolean;
}

interface CreateUserForm {
  name: string;
  email: string;
  password: string;
  phone: string;
  business_name: string;
  merchant_id: string;
  api_key: string;
  api_secret: string;
  role_id: number;
  selected_manager_id: string;
  viyonaEnabled: boolean;
  poolpayEnabled: boolean;
  addMoneyEnabled: boolean;
  adminControlsPayoutOptions: boolean;
  unauthorizedIpDetected: boolean;
}

interface FraudMerchantEntry {
  id: string;
  merchantId: string;
  merchantName: string;
  merchantEmail: string;
  merchantPhone?: string;
  reason: string;
  createdAt: string;
}

const UserManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'managers' | 'merchants' | 'admins'>('managers');
  const [managers, setManagers] = useState<User[]>([]);
  const [merchants, setMerchants] = useState<User[]>([]);
  const [admins, setAdmins] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createUserType, setCreateUserType] = useState<'manager' | 'merchant' | 'admin'>('manager');
  
  // Pagination
  const [managersPage, setManagersPage] = useState(1);
  const [merchantsPage, setMerchantsPage] = useState(1);
  const [adminsPage, setAdminsPage] = useState(1);
  const [managersTotalPages, setManagersTotalPages] = useState(1);
  const [merchantsTotalPages, setMerchantsTotalPages] = useState(1);
  const [adminsTotalPages, setAdminsTotalPages] = useState(1);
  const [managersTotalCount, setManagersTotalCount] = useState(0);
  const [merchantsTotalCount, setMerchantsTotalCount] = useState(0);
  const [adminsTotalCount, setAdminsTotalCount] = useState(0);
  const limit = 10;

  // Search
  const [managersSearch, setManagersSearch] = useState('');
  const [merchantsSearch, setMerchantsSearch] = useState('');
  const [adminsSearch, setAdminsSearch] = useState('');

  // Create form
  const [formData, setFormData] = useState<CreateUserForm>({
    name: '',
    email: '',
    password: '',
    phone: '',
    business_name: '',
    merchant_id: '',
    api_key: '',
    api_secret: '',
    role_id: 2, // manager
    selected_manager_id: '',
    viyonaEnabled: true,
    poolpayEnabled: true,
    addMoneyEnabled: true,
    adminControlsPayoutOptions: false,
    unauthorizedIpDetected: true
  });
  const [formErrors, setFormErrors] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);

  // Modal states for actions
  const [showToggleModal, setShowToggleModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editForm, setEditForm] = useState<{ name: string; email: string; phone: string }>({
    name: '',
    email: '',
    phone: ''
  });
  const [editApiForm, setEditApiForm] = useState<{ client_id: string; api_key: string; client_secret: string }>({
    client_id: '',
    api_key: '',
    client_secret: ''
  });
  const [editToggleForm, setEditToggleForm] = useState<{ viyonaEnabled: boolean; poolpayEnabled: boolean; addMoneyEnabled: boolean; adminControlsPayoutOptions: boolean }>({
    viyonaEnabled: true,
    poolpayEnabled: true,
    addMoneyEnabled: true,
    adminControlsPayoutOptions: false
  });

  // Merchants-by-manager modal
  const [showMerchantsModal, setShowMerchantsModal] = useState(false);
  const [selectedManagerForMerchants, setSelectedManagerForMerchants] = useState<User | null>(null);
  const [managerMerchants, setManagerMerchants] = useState<User[]>([]);
  const [loadingManagerMerchants, setLoadingManagerMerchants] = useState(false);

  // Notification modal (replaces browser alert)
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationType, setNotificationType] = useState<'success' | 'error'>('success');
  const [showBannedConfirmModal, setShowBannedConfirmModal] = useState(false);
  const [bannedTargetUser, setBannedTargetUser] = useState<User | null>(null);
  const [bannedReason, setBannedReason] = useState('');
  const [showBlacklistConfirmModal, setShowBlacklistConfirmModal] = useState(false);
  const [blacklistTargetUser, setBlacklistTargetUser] = useState<User | null>(null);
  const [blacklistReason, setBlacklistReason] = useState('');
  const [blacklistReasonError, setBlacklistReasonError] = useState('');
  const [toggleSubmitting, setToggleSubmitting] = useState(false);
  const [bannedMerchantIds, setBannedMerchantIds] = useState<Set<string>>(new Set());
  const [blacklistedMerchantIds, setBlacklistedMerchantIds] = useState<Set<string>>(new Set());
  const [adminUnauthorizedIpOverrides, setAdminUnauthorizedIpOverrides] = useState<Record<number, boolean>>({});

  useEffect(() => {
    fetchManagers();
  }, [managersPage, managersSearch]);

  useEffect(() => {
    fetchMerchants();
  }, [merchantsPage, merchantsSearch]);

  useEffect(() => {
    fetchAdmins();
  }, [adminsPage, adminsSearch]);

  // Fetch admin count once
  useEffect(() => {
    fetchAdminsCount();
  }, []);

  useEffect(() => {
    loadFraudMerchantStatus();
  }, []);

  useEffect(() => {
    try {
      const raw = localStorage.getItem('admin_unauthorised_ip_overrides_v1');
      setAdminUnauthorizedIpOverrides(raw ? JSON.parse(raw) : {});
    } catch (error) {
      setAdminUnauthorizedIpOverrides({});
    }
  }, []);

  const isAdminUnauthorizedIpEnabled = (user: User) => {
    if (Object.prototype.hasOwnProperty.call(adminUnauthorizedIpOverrides, user.id)) {
      return adminUnauthorizedIpOverrides[user.id] === true;
    }
    return user.unauthorised_ip_detected === true || user.unauthorised_ip_detected === 1;
  };

  const handleAdminUnauthorizedIpToggle = async (userId: number, enabled: boolean) => {
    setAdminUnauthorizedIpOverrides(prev => {
      const next = { ...prev, [userId]: enabled };
      localStorage.setItem('admin_unauthorised_ip_overrides_v1', JSON.stringify(next));
      return next;
    });
    setAdmins(prev =>
      prev.map(admin =>
        admin.id === userId
          ? { ...admin, unauthorised_ip_detected: enabled ? 1 : 0 }
          : admin
      )
    );

    try {
      await adminAPI.updateUser(userId, { unauthorised_ip_detected: enabled });
    } catch (error) {
      console.error('Error updating unauthorized IP detection setting:', error);
      setNotificationMessage('Failed to update unauthorized IP detection setting.');
      setNotificationType('error');
      setShowNotificationModal(true);
    }
  };

  const fetchManagers = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getAllUsers({
        page: managersPage,
        limit,
        role: 'manager',
        search: managersSearch,
        sync_wallet: true
      });
      
      setManagers(response.data.data.users);
      setManagersTotalPages(response.data.data.pagination.totalPages);
      setManagersTotalCount(response.data.data.pagination.totalRecords || 0);
    } catch (error) {
      console.error('Error fetching managers:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMerchants = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getAllUsers({
        page: merchantsPage,
        limit,
        role: 'merchant',
        search: merchantsSearch
      });
      
      setMerchants(response.data.data.users);
      setMerchantsTotalPages(response.data.data.pagination.totalPages);
      setMerchantsTotalCount(response.data.data.pagination.totalRecords || 0);
    } catch (error) {
      console.error('Error fetching merchants:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAdminsCount = async () => {
    try {
      const response = await adminAPI.getAllUsers({
        page: 1,
        limit: 1,
        role: 'admin'
      });
      setAdminsTotalCount(response.data.data.pagination.totalRecords || 0);
    } catch (error) {
      console.error('Error fetching admins count:', error);
      setAdminsTotalCount(0);
    }
  };

  const fetchAdmins = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getAllUsers({
        page: adminsPage,
        limit,
        role: 'admin',
        search: adminsSearch
      });

      setAdmins(response.data.data.users || []);
      setAdminsTotalPages(response.data.data.pagination.totalPages || 1);
      setAdminsTotalCount(response.data.data.pagination.totalRecords || 0);
    } catch (error) {
      console.error('Error fetching admins:', error);
      setAdmins([]);
      setAdminsTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  const loadFraudMerchantStatus = () => {
    try {
      const bannedRaw = localStorage.getItem('admin_fraud_banned_merchants_v1');
      const blacklistedRaw = localStorage.getItem('admin_fraud_blacklisted_merchants_v1');
      const banned: FraudMerchantEntry[] = bannedRaw ? JSON.parse(bannedRaw) : [];
      const blacklisted: FraudMerchantEntry[] = blacklistedRaw ? JSON.parse(blacklistedRaw) : [];
      setBannedMerchantIds(new Set(banned.map(entry => String(entry.merchantId).trim())));
      setBlacklistedMerchantIds(new Set(blacklisted.map(entry => String(entry.merchantId).trim())));
    } catch (error) {
      console.error('Error loading fraud merchant status:', error);
      setBannedMerchantIds(new Set());
      setBlacklistedMerchantIds(new Set());
    }
  };

  const handleToggleStatus = async (userId: number, currentStatus: boolean) => {
    const user = [...managers, ...merchants, ...admins].find(u => u.id === userId);
    if (user) {
      setSelectedUser(user);
      setShowToggleModal(true);
    }
  };

  const confirmToggleStatus = async () => {
    if (!selectedUser || toggleSubmitting) return;

    try {
      setToggleSubmitting(true);
      const nextStatus = !selectedUser.is_active;
      try {
        await adminAPI.toggleUserStatus(selectedUser.id, nextStatus);
      } catch (toggleError) {
        // Fallback for environments where PATCH toggle route is blocked/unavailable.
        await adminAPI.updateUser(selectedUser.id, { is_active: nextStatus });
      }

      // Refresh the relevant list from user role to avoid stale active tab assumptions
      if ((selectedUser.role_name || '').toLowerCase() === 'manager') {
        fetchManagers();
      } else if ((selectedUser.role_name || '').toLowerCase() === 'admin') {
        fetchAdmins();
      } else {
        fetchMerchants();
      }

      setNotificationMessage(
        `${selectedUser.name} has been ${selectedUser.is_active ? 'deactivated' : 'activated'} successfully.`
      );
      setNotificationType('success');
      setShowNotificationModal(true);
      setShowToggleModal(false);
      setSelectedUser(null);
    } catch (error: any) {
      console.error('Error toggling user status:', error);
      const serverMessage = error?.response?.data?.message;
      const httpStatus = error?.response?.status;
      setNotificationMessage(
        serverMessage || (httpStatus ? `Failed to update user status (HTTP ${httpStatus}).` : 'Failed to update user status. Please try again.')
      );
      setNotificationType('error');
      setShowNotificationModal(true);
    } finally {
      setToggleSubmitting(false);
    }
  };

  const handleResetPassword = async (userId: number, userName: string) => {
    const user = [...managers, ...merchants, ...admins].find(u => u.id === userId);
    if (user) {
      setSelectedUser(user);
      setNewPassword('');
      setShowPasswordModal(true);
    }
  };

  const confirmResetPassword = async () => {
    if (!selectedUser || !newPassword) return;

    if (newPassword.length < 6) {
      return;
    }

    try {
      await adminAPI.resetUserPassword(selectedUser.id, newPassword);
      setShowPasswordModal(false);
      setSelectedUser(null);
      setNewPassword('');
    } catch (error) {
      console.error('Error resetting password:', error);
    }
  };

  const handleDeleteUser = async (userId: number, userName: string) => {
    const user = [...managers, ...merchants, ...admins].find(u => u.id === userId);
    if (user) {
      setSelectedUser(user);
      setShowDeleteModal(true);
    }
  };

  const handleFraudTag = (user: User, target: 'banned' | 'blacklisted') => {
    if ((user.role_name || '').toLowerCase() !== 'merchant') {
      setNotificationMessage('Only merchants can be tagged as banned or blacklisted.');
      setNotificationType('error');
      setShowNotificationModal(true);
      return;
    }

    try {
      const storageKey =
        target === 'banned' ? 'admin_fraud_banned_merchants_v1' : 'admin_fraud_blacklisted_merchants_v1';
      const raw = localStorage.getItem(storageKey);
      const existing: FraudMerchantEntry[] = raw ? JSON.parse(raw) : [];
      const merchantId = String(user.id);
      const alreadyExists = existing.some(entry => String(entry.merchantId).trim() === merchantId);

      if (alreadyExists) {
        setNotificationMessage(
          `${user.name} is already in ${target === 'banned' ? 'Banned Merchants' : 'Blacklisted Merchants'}.`
        );
        setNotificationType('error');
        setShowNotificationModal(true);
        return;
      }

      if (target === 'banned') {
        setBannedTargetUser(user);
        setBannedReason('');
        setShowBannedConfirmModal(true);
        return;
      }

      if (target === 'blacklisted') {
        setBlacklistTargetUser(user);
        setBlacklistReason('');
        setBlacklistReasonError('');
        setShowBlacklistConfirmModal(true);
        return;
      }

      const newEntry: FraudMerchantEntry = {
        id: `${Date.now()}_${Math.random().toString(16).slice(2)}`,
        merchantId,
        merchantName: user.name,
        merchantEmail: user.email,
        merchantPhone: user.phone || '',
        reason: `Added from User Management (${target})`,
        createdAt: new Date().toISOString(),
      };

      localStorage.setItem(storageKey, JSON.stringify([newEntry, ...existing]));
      loadFraudMerchantStatus();
      setNotificationMessage(
        `${user.name} added to ${target === 'banned' ? 'Banned Merchants' : 'Blacklisted Merchants'} successfully.`
      );
      setNotificationType('success');
      setShowNotificationModal(true);
    } catch (error) {
      console.error(`Error adding merchant to ${target} list:`, error);
      setNotificationMessage(`Failed to add ${user.name} to ${target} list.`);
      setNotificationType('error');
      setShowNotificationModal(true);
    }
  };

  const confirmAddToBlacklisted = async () => {
    if (!blacklistTargetUser) return;

    const reason = blacklistReason.trim();
    if (!reason) {
      setBlacklistReasonError('Blacklisted reason is required');
      return;
    }

    try {
      await adminAPI.updateUser(blacklistTargetUser.id, { payout_blacklisted: true });

      const storageKey = 'admin_fraud_blacklisted_merchants_v1';
      const raw = localStorage.getItem(storageKey);
      const existing: FraudMerchantEntry[] = raw ? JSON.parse(raw) : [];
      const merchantId = String(blacklistTargetUser.id);
      const alreadyExists = existing.some(entry => String(entry.merchantId).trim() === merchantId);

      if (alreadyExists) {
        setShowBlacklistConfirmModal(false);
        setBlacklistTargetUser(null);
        setNotificationMessage(`${blacklistTargetUser.name} is already in Blacklisted Merchants.`);
        setNotificationType('error');
        setShowNotificationModal(true);
        return;
      }

      const newEntry: FraudMerchantEntry = {
        id: `${Date.now()}_${Math.random().toString(16).slice(2)}`,
        merchantId,
        merchantName: blacklistTargetUser.name,
        merchantEmail: blacklistTargetUser.email,
        merchantPhone: blacklistTargetUser.phone || '',
        reason,
        createdAt: new Date().toISOString(),
      };

      localStorage.setItem(storageKey, JSON.stringify([newEntry, ...existing]));
      loadFraudMerchantStatus();

      setShowBlacklistConfirmModal(false);
      setBlacklistTargetUser(null);
      setBlacklistReason('');
      setBlacklistReasonError('');

      setNotificationMessage(`${newEntry.merchantName} added to Blacklisted Merchants successfully.`);
      setNotificationType('success');
      setShowNotificationModal(true);
    } catch (error: any) {
      console.error('Error adding merchant to blacklisted list:', error);
      setNotificationMessage(
        error?.response?.data?.message || 'Failed to add merchant to Blacklisted Merchants (server error).'
      );
      setNotificationType('error');
      setShowNotificationModal(true);
    }
  };

  const confirmAddToBanned = async () => {
    if (!bannedTargetUser) return;

    try {
      await adminAPI.updateUser(bannedTargetUser.id, { is_active: false });

      const storageKey = 'admin_fraud_banned_merchants_v1';
      const raw = localStorage.getItem(storageKey);
      const existing: FraudMerchantEntry[] = raw ? JSON.parse(raw) : [];
      const merchantId = String(bannedTargetUser.id);
      const alreadyExists = existing.some(entry => String(entry.merchantId).trim() === merchantId);

      if (alreadyExists) {
        setShowBannedConfirmModal(false);
        setBannedTargetUser(null);
        setNotificationMessage(`${bannedTargetUser.name} is already in Banned Merchants.`);
        setNotificationType('error');
        setShowNotificationModal(true);
        return;
      }

      const newEntry: FraudMerchantEntry = {
        id: `${Date.now()}_${Math.random().toString(16).slice(2)}`,
        merchantId,
        merchantName: bannedTargetUser.name,
        merchantEmail: bannedTargetUser.email,
        merchantPhone: bannedTargetUser.phone || '',
        reason: bannedReason.trim() || 'Added from User Management (banned)',
        createdAt: new Date().toISOString(),
      };

      localStorage.setItem(storageKey, JSON.stringify([newEntry, ...existing]));
      loadFraudMerchantStatus();

      setShowBannedConfirmModal(false);
      setBannedTargetUser(null);
      setBannedReason('');

      setNotificationMessage(`${newEntry.merchantName} added to Banned Merchants successfully.`);
      setNotificationType('success');
      setShowNotificationModal(true);
    } catch (error: any) {
      console.error('Error adding merchant to banned list:', error);
      setNotificationMessage(
        error?.response?.data?.message || 'Failed to ban merchant on server. They can still log in until this succeeds.'
      );
      setNotificationType('error');
      setShowNotificationModal(true);
    }
  };

  const confirmDeleteUser = async () => {
    if (!selectedUser) return;

    try {
      await adminAPI.deleteUser(selectedUser.id);
      
      // Refresh the appropriate list
      if (activeTab === 'managers') {
        fetchManagers();
      } else if (activeTab === 'admins') {
        fetchAdmins();
      } else {
        fetchMerchants();
      }
      
      setShowDeleteModal(false);
      setSelectedUser(null);
    } catch (error) {
      console.error('Error deleting user:', error);
    }
  };

  const handleCreateUser = (type: 'manager' | 'merchant' | 'admin') => {
    setCreateUserType(type);
    setFormData({
      name: '',
      email: '',
      password: '',
      phone: '',
      business_name: '',
      merchant_id: '',
      api_key: '',
      api_secret: '',
      role_id: type === 'manager' ? 2 : type === 'admin' ? 1 : 4, // 1 admin, 2 manager, 4 merchant
      selected_manager_id: '',
      viyonaEnabled: true,
      poolpayEnabled: true,
      addMoneyEnabled: true,
      adminControlsPayoutOptions: false,
      unauthorizedIpDetected: true
    });
    setFormErrors([]);
    setShowCreateModal(true);
  };

  const validateForm = (): boolean => {
    const errors: string[] = [];

    if (!formData.name.trim()) {
      errors.push('Name is required');
    }

    if (!formData.email.trim()) {
      errors.push('Email is required');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.push('Invalid email format');
    }

    if (!formData.password) {
      errors.push('Password is required');
    } else if (formData.password.length < 6) {
      errors.push('Password must be at least 6 characters long');
    }

    if (createUserType === 'manager') {
      if (!formData.phone.trim()) {
        errors.push('Mobile number is required');
      } else if (formData.phone.replace(/\D/g, '').length < 10) {
        errors.push('Please enter a valid 10-digit mobile number');
      }
      if (!formData.merchant_id.trim()) {
        errors.push('Client ID is required for managers');
      }
      if (!formData.api_key.trim()) {
        errors.push('API Key is required for managers');
      }
      if (!formData.api_secret.trim()) {
        errors.push('Client Secret is required for managers');
      }
    }

    if (createUserType === 'merchant') {
      if (!formData.selected_manager_id.trim()) {
        errors.push('Please select a manager for this merchant');
      }
      if (!formData.phone.trim()) {
        errors.push('Mobile number is required');
      } else if (formData.phone.replace(/\D/g, '').length < 10) {
        errors.push('Please enter a valid 10-digit mobile number');
      }
    }

    if (createUserType === 'admin') {
      if (!formData.phone.trim()) {
        errors.push('Mobile number is required');
      } else if (formData.phone.replace(/\D/g, '').length < 10) {
        errors.push('Please enter a valid 10-digit mobile number');
      }
    }

    setFormErrors(errors);
    return errors.length === 0;
  };

  const handleSubmitCreate = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      const payload: any = { ...formData };
      if (createUserType === 'merchant') {
        payload.viyona_enabled = formData.viyonaEnabled;
        payload.poolpay_enabled = formData.poolpayEnabled;
        payload.add_money_enabled = formData.addMoneyEnabled;
        payload.admin_controls_payout_options = formData.adminControlsPayoutOptions;
      } else if (createUserType === 'admin') {
        payload.unauthorised_ip_detected = formData.unauthorizedIpDetected;
      }
      await adminAPI.createUser(payload);
      setShowCreateModal(false);
      setNotificationMessage(
        `${createUserType === 'manager' ? 'Manager' : createUserType === 'admin' ? 'Admin' : 'Merchant'} created successfully!`
      );
      setNotificationType('success');
      setShowNotificationModal(true);
      // Refresh the appropriate list
      if (createUserType === 'manager') {
        fetchManagers();
      } else if (createUserType === 'admin') {
        fetchAdmins();
      } else {
        fetchMerchants();
      }
    } catch (error: any) {
      console.error('Error creating user:', error);
      setNotificationMessage(error.response?.data?.message || 'Failed to create user');
      setNotificationType('error');
      setShowNotificationModal(true);
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderUserTable = (users: User[], type: 'managers' | 'merchants' | 'admins') => {
    console.log(`Rendering ${type} table with ${users.length} users:`, users);
    
    if (loading) {
      return (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading {type}...</p>
        </div>
      );
    }

    if (users.length === 0) {
      return (
        <div className="empty-state">
          <span className="empty-icon">📭</span>
          <h3>No {type} found</h3>
          <p>Create your first {type === 'managers' ? 'manager' : type === 'admins' ? 'admin' : 'merchant'} to get started</p>
          <button 
            className="btn-primary"
            onClick={() => handleCreateUser(type === 'managers' ? 'manager' : type === 'admins' ? 'admin' : 'merchant')}
          >
            Create {type === 'managers' ? 'Manager' : type === 'admins' ? 'Admin' : 'Merchant'}
          </button>
        </div>
      );
    }

    return (
      <div className="table-container">
        <table className={`transaction-table ${type}-table`}>
          <thead>
            <tr>
              <th>ID</th>
              <th>{type === 'managers' ? 'MANAGER NAME' : type === 'admins' ? 'ADMIN NAME' : 'MERCHANT NAME'}</th>
              <th>MOBILE</th>
              {type !== 'admins' && <th>WALLET BALANCE</th>}
              {type === 'managers' && <th>ACCOUNT BALANCE</th>}
              {type === 'managers' && <th>MERCHANTS</th>}
              {type === 'merchants' && <th>PAYOUTS</th>}
              <th>STATUS</th>
              {type === 'merchants' && <th>IS BANNED</th>}
              {type === 'merchants' && <th>IS BLACKLISTED</th>}
              <th>CREATED / LAST LOGIN</th>
              <th>ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {users.map(user => (
              <tr key={user.id} className={!user.is_active ? 'inactive-row' : ''}>
                <td>{user.id}</td>
                <td>
                  <div className="user-info1">
                    <strong>{user.name}</strong>
                    <span className="user-email">{user.email}</span>
                  </div>
                </td>
                <td>{user.phone || '-'}</td>
                {type !== 'admins' && <td className="currency">{formatCurrency(user.wallet_balance)}</td>}
                {type === 'managers' && <td className="currency">{formatCurrency(user.account_balance || 0)}</td>}
                {type === 'managers' && (
                  <td className="center">
                    <button
                      className="btn-link"
                      onClick={async () => {
                        setSelectedManagerForMerchants(user);
                        setShowMerchantsModal(true);
                        try {
                          setLoadingManagerMerchants(true);
                          const resp = await adminAPI.getAllUsers({ role: 'merchant', created_by: user.id, page: 1, limit: 100 });
                          setManagerMerchants(resp.data.data.users || []);
                        } catch (e) {
                          console.error('Error loading manager merchants', e);
                          setManagerMerchants([]);
                        } finally {
                          setLoadingManagerMerchants(false);
                        }
                      }}
                      title="View merchants created by this manager"
                    >
                      {user.merchants_created ?? 0}
                    </button>
                  </td>
                )}
                {type === 'merchants' && <td className="center">{user.total_payouts || 0}</td>}
                <td>
                  {(() => {
                    const isMerchant = type === 'merchants';
                    const isBanned = isMerchant && bannedMerchantIds.has(String(user.id));
                    const isBlacklisted = isMerchant && blacklistedMerchantIds.has(String(user.id));
                    // Blacklisted merchants should stay Active (login allowed), but Banned should show Inactive.
                    const showActive = isBlacklisted ? user.is_active : (!isBanned && user.is_active);
                    return (
                      <span className={`status-badge ${showActive ? 'active' : 'inactive'}`}>
                        {showActive ? '✓ Active' : '✗ Inactive'}
                      </span>
                    );
                  })()}
                </td>
                {type === 'merchants' && (
                  <td>
                    {bannedMerchantIds.has(String(user.id)) ? (
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          padding: '3px 10px',
                          borderRadius: 999,
                          fontSize: 11,
                          fontWeight: 700,
                          letterSpacing: '0.04em',
                          color: '#ffffff',
                          background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                        }}
                      >
                        YES
                      </span>
                    ) : (
                      <span style={{ color: '#64748b', fontWeight: 600 }}>NO</span>
                    )}
                  </td>
                )}
                {type === 'merchants' && (
                  <td>
                    {blacklistedMerchantIds.has(String(user.id)) ? (
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          padding: '3px 10px',
                          borderRadius: 999,
                          fontSize: 11,
                          fontWeight: 700,
                          letterSpacing: '0.04em',
                          color: '#ffffff',
                          background: 'linear-gradient(135deg, #7c3aed, #6d28d9)',
                        }}
                      >
                        YES
                      </span>
                    ) : (
                      <span style={{ color: '#64748b', fontWeight: 600 }}>NO</span>
                    )}
                  </td>
                )}
                <td className="date">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <span>{formatDate(user.created_at)}</span>
                    <span style={{ color: '#64748b' }}>{formatDate(user.last_login)}</span>
                  </div>
                </td>
                <td className="actions-cell">
                  {type === 'admins' ? (
                    <div className="action-buttons">
                      <button
                        className="btn-icon btn-info"
                        onClick={() => handleResetPassword(user.id, user.name)}
                        title="Reset Password"
                      >
                        🔑
                      </button>
                      <button
                        className="btn-icon btn-primary"
                        onClick={() => {
                          setSelectedUser(user);
                          setEditForm({
                            name: user.name || '',
                            email: user.email || '',
                            phone: user.phone || ''
                          });
                          setShowEditModal(true);
                        }}
                        title="Change Email"
                      >
                        📧
                      </button>
                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 600 }}>
                        <input
                          type="checkbox"
                          checked={isAdminUnauthorizedIpEnabled(user)}
                          onChange={(e) => handleAdminUnauthorizedIpToggle(user.id, e.target.checked)}
                        />
                        UNAUTHORISED IP DETECTED
                      </label>
                    </div>
                  ) : (
                    <div className="action-buttons">
                      <button
                        className="btn-icon btn-primary"
                        onClick={async () => {
                          setSelectedUser(user);
                          // For merchants, fetch full details to get correct toggle values (list may be stale)
                          let userData = user;
                          if (user.role_name === 'merchant') {
                            try {
                              const resp = await adminAPI.getUserById(user.id);
                              if (resp.data?.success && resp.data?.data?.user) {
                                userData = resp.data.data.user;
                              }
                            } catch (e) {
                              console.error('Error fetching merchant details for edit', e);
                            }
                          }
                          setEditForm({
                            name: userData.name || '',
                            email: userData.email || '',
                            phone: userData.phone || ''
                          });
                          setEditToggleForm({
                            viyonaEnabled: userData.viyona_enabled !== 0 && userData.viyona_enabled !== false,
                            poolpayEnabled: userData.poolpay_enabled !== 0 && userData.poolpay_enabled !== false,
                            addMoneyEnabled: userData.add_money_enabled !== 0 && userData.add_money_enabled !== false,
                            adminControlsPayoutOptions: userData.admin_controls_payout_options === 1 || userData.admin_controls_payout_options === true
                          });
                          setEditApiForm({
                            client_id: userData.client_id || '',
                            api_key: userData.api_key || '',
                            client_secret: userData.client_secret || ''
                          });
                          setShowEditModal(true);
                        }}
                        title="Edit User"
                      >
                        ✏️
                      </button>
                      <button
                        className={`btn-icon ${user.is_active ? 'btn-warning' : 'btn-success'}`}
                        onClick={() => handleToggleStatus(user.id, user.is_active)}
                        title={user.is_active ? 'Deactivate' : 'Activate'}
                      >
                        {user.is_active ? '🔒' : '🔓'}
                      </button>
                      <button
                        className="btn-icon btn-warning"
                        onClick={() => handleFraudTag(user, 'banned')}
                        title="Add to Banned Merchants"
                      >
                        🚫
                      </button>
                      <button
                        className="btn-icon btn-primary"
                        onClick={() => handleFraudTag(user, 'blacklisted')}
                        title="Add to Blacklisted Merchants"
                      >
                        ⛔
                      </button>
                      <button
                        className="btn-icon btn-info"
                        onClick={() => handleResetPassword(user.id, user.name)}
                        title="Reset Password"
                      >
                        🔑
                      </button>
                      <button
                        className="btn-icon btn-danger"
                        onClick={() => handleDeleteUser(user.id, user.name)}
                        title="Delete User"
                      >
                        🗑️
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderPagination = (currentPage: number, totalPages: number, onPageChange: (page: number) => void) => {
    if (totalPages <= 1) return null;

    return (
      <div className="pagination">
        <button
          className="pagination-btn"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          ← Previous
        </button>
        <span className="pagination-info">
          Page {currentPage} of {totalPages}
        </span>
        <button
          className="pagination-btn"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          Next →
        </button>
      </div>
    );
  };

  return (
    <div className="user-management">
      <div className="page-header">
        <div className="header-content">
          <h1>👥 User Management</h1>
          <p>Manage managers, merchants and admins</p>
        </div>
        <div className="header-actions">
          {activeTab === 'managers' && (
            <button 
              className="btn-primary"
              onClick={() => handleCreateUser('manager')}
            >
              ➕ Create Manager
            </button>
          )}
          {activeTab === 'merchants' && (
            <button 
              className="btn-primary"
              onClick={() => handleCreateUser('merchant')}
            >
              ➕ Create Merchant
            </button>
          )}
          {activeTab === 'admins' && (
            <button 
              className="btn-primary"
              onClick={() => handleCreateUser('admin')}
            >
              ➕ Create Admin
            </button>
          )}
        </div>
      </div>

      {/* Summary Counts */}
      <div className="summary-cards">
        <div className="summary-card credit">
          <div className="card-icon">👔</div>
          <div className="card-content">
            <h3>TOTAL MANAGERS</h3>
            <p className="card-value">{managersTotalCount}</p>
          </div>
        </div>
        <div className="summary-card credit">
          <div className="card-icon">🏪</div>
          <div className="card-content">
            <h3>TOTAL MERCHANTS</h3>
            <p className="card-value">{merchantsTotalCount}</p>
          </div>
        </div>
        <div className="summary-card credit">
          <div className="card-icon">👑</div>
          <div className="card-content">
            <h3>TOTAL ADMINS</h3>
            <p className="card-value">{adminsTotalCount}</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs">
        <button
          className={`tab ${activeTab === 'managers' ? 'active' : ''}`}
          onClick={() => setActiveTab('managers')}
        >
          <span className="tab-icon">👔</span>
          <span className="tab-label">Managers</span>
          <span className="tab-count">{managers.length}</span>
        </button>
        <button
          className={`tab ${activeTab === 'merchants' ? 'active' : ''}`}
          onClick={() => setActiveTab('merchants')}
        >
          <span className="tab-icon">🏪</span>
          <span className="tab-label">Merchants</span>
          <span className="tab-count">{merchants.length}</span>
        </button>
        <button
          className={`tab ${activeTab === 'admins' ? 'active' : ''}`}
          onClick={() => setActiveTab('admins')}
          type="button"
        >
          <span className="tab-icon">👑</span>
          <span className="tab-label">Admins</span>
          <span className="tab-count">{admins.length}</span>
        </button>
      </div>

      {/* Search Bar */}
      <div className="search-container">
        <input
          type="text"
          className="search-input"
          placeholder={`Search ${activeTab}...`}
          value={activeTab === 'managers' ? managersSearch : activeTab === 'admins' ? adminsSearch : merchantsSearch}
          onChange={(e) => {
            if (activeTab === 'managers') {
              setManagersSearch(e.target.value);
              setManagersPage(1);
            } else if (activeTab === 'admins') {
              setAdminsSearch(e.target.value);
              setAdminsPage(1);
            } else {
              setMerchantsSearch(e.target.value);
              setMerchantsPage(1);
            }
          }}
        />
      </div>

      {/* User Tables */}
      {activeTab === 'managers' && (
        <>
          {renderUserTable(managers, 'managers')}
          {renderPagination(managersPage, managersTotalPages, setManagersPage)}
        </>
      )}

      {activeTab === 'merchants' && (
        <>
          {renderUserTable(merchants, 'merchants')}
          {renderPagination(merchantsPage, merchantsTotalPages, setMerchantsPage)}
        </>
      )}

      {activeTab === 'admins' && (
        <>
          {renderUserTable(admins, 'admins')}
          {renderPagination(adminsPage, adminsTotalPages, setAdminsPage)}
        </>
      )}

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className={`modal-content1 ${createUserType === 'merchant' ? 'modal-landscape' : ''}`} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create {createUserType === 'manager' ? 'Manager' : createUserType === 'admin' ? 'Admin' : 'Merchant'}</h2>
              <button className="modal-close" onClick={() => setShowCreateModal(false)}>✕</button>
            </div>

            <form onSubmit={handleSubmitCreate}>
              <div className="modal-body1">
                {formErrors.length > 0 && (
                  <div className="error-messages">
                    {formErrors.map((error, index) => (
                      <div key={index} className="error-message">⚠️ {error}</div>
                    ))}
                  </div>
                )}

                {createUserType === 'merchant' ? (
                  <div className="create-merchant-layout">
                    <div className="create-merchant-left">
                    <div className="form-group">
                      <label htmlFor="selected_manager_id">Choose From Managers List *</label>
                      <select
                        id="selected_manager_id"
                        className="form-control"
                        value={formData.selected_manager_id}
                        onChange={(e) => setFormData({ ...formData, selected_manager_id: e.target.value })}
                      >
                        <option value="">Select a manager...</option>
                        {managers.map((manager) => (
                          <option key={manager.id} value={manager.id}>
                            {manager.name} ({manager.email})
                          </option>
                        ))}
                      </select>
                    </div>
                    {formData.selected_manager_id && (() => {
                      const selectedManager = managers.find((m) => String(m.id) === String(formData.selected_manager_id));
                      return selectedManager ? (
                        <div className="manager-api-fields">
                          <small className="form-text form-text-section">From selected manager (will be assigned to merchant)</small>
                          <div className="form-group">
                            <label>Client ID</label>
                            <div className="form-control-with-lock">
                              <input
                                type="text"
                                className="form-control"
                                value={selectedManager.client_id || '—'}
                                readOnly
                                style={{ backgroundColor: 'var(--input-readonly-bg, #f5f5f5)', cursor: 'default' }}
                              />
                              <span className="input-lock-icon" title="Read-only (from manager)">🔒</span>
                            </div>
                          </div>
                          <div className="form-group">
                            <label>Client Secret</label>
                            <div className="form-control-with-lock">
                              <input
                                type="text"
                                className="form-control"
                                value={selectedManager.client_secret || '—'}
                                readOnly
                                style={{ backgroundColor: 'var(--input-readonly-bg, #f5f5f5)', cursor: 'default' }}
                              />
                              <span className="input-lock-icon" title="Read-only (from manager)">🔒</span>
                            </div>
                          </div>
                          <div className="form-group">
                            <label>API Key</label>
                            <div className="form-control-with-lock">
                              <input
                                type="text"
                                className="form-control"
                                value={selectedManager.api_key || '—'}
                                readOnly
                                style={{ backgroundColor: 'var(--input-readonly-bg, #f5f5f5)', cursor: 'default' }}
                              />
                              <span className="input-lock-icon" title="Read-only (from manager)">🔒</span>
                            </div>
                          </div>
                        </div>
                        ) : null;
                    })()}
                      <div className="form-group">
                        <label htmlFor="name">Full Name *</label>
                        <input
                          type="text"
                          id="name"
                          className="form-control"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          placeholder="Enter full name"
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="email">Email Address *</label>
                        <input
                          type="email"
                          id="email"
                          className="form-control"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          placeholder="Enter email address"
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="password">Password *</label>
                        <input
                          type="password"
                          id="password"
                          className="form-control"
                          value={formData.password}
                          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                          placeholder="Enter password (min 6 characters)"
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="phone">Mobile Number *</label>
                        <input
                          type="tel"
                          id="phone"
                          className="form-control"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value.replace(/\D/g, '').slice(0, 15) })}
                          placeholder="Enter 10-digit mobile number"
                          maxLength={15}
                        />
                      </div>
                    </div>
                    <div className="create-merchant-right">
                  <div className="form-group payout-options-section">
                    <label className="payout-options-label">Payout Options</label>
                    <div className="form-group admin-controls-row">
                      <div className="admin-controls-checkbox-row">
                        <input
                          type="checkbox"
                          id="create-admin-controls-cb"
                          checked={formData.adminControlsPayoutOptions}
                          onChange={(e) => setFormData(prev => ({ ...prev, adminControlsPayoutOptions: e.target.checked }))}
                        />
                        <label htmlFor="create-admin-controls-cb" className="admin-controls-checkbox-label">Admin controls payout options</label>
                      </div>
                      <small className="admin-controls-description">Manager cannot enable/disable these toggles when checked.</small>
                    </div>
                    <div className="payout-toggles-grid">
                      <div className="payout-toggle-row">
                        <span className="payout-toggle-label">Viyona Payout</span>
                        <button
                          type="button"
                          role="switch"
                          aria-checked={formData.viyonaEnabled}
                          className={`payout-toggle-btn ${formData.viyonaEnabled ? 'on' : 'off'}`}
                          onClick={() => setFormData(prev => ({ ...prev, viyonaEnabled: !prev.viyonaEnabled }))}
                        >
                          <span className="payout-toggle-knob" />
                        </button>
                      </div>
                      <div className="payout-toggle-row">
                        <span className="payout-toggle-label">PoolPay Payout</span>
                        <button
                          type="button"
                          role="switch"
                          aria-checked={formData.poolpayEnabled}
                          className={`payout-toggle-btn ${formData.poolpayEnabled ? 'on' : 'off'}`}
                          onClick={() => setFormData(prev => ({ ...prev, poolpayEnabled: !prev.poolpayEnabled }))}
                        >
                          <span className="payout-toggle-knob" />
                        </button>
                      </div>
                      <div className="payout-toggle-row">
                        <span className="payout-toggle-label">Add Money To Merchant</span>
                        <button
                          type="button"
                          role="switch"
                          aria-checked={formData.addMoneyEnabled}
                          className={`payout-toggle-btn ${formData.addMoneyEnabled ? 'on' : 'off'}`}
                          onClick={() => setFormData(prev => ({ ...prev, addMoneyEnabled: !prev.addMoneyEnabled }))}
                        >
                          <span className="payout-toggle-knob" />
                        </button>
                      </div>
                    </div>
                    <small className="payout-options-hint">Disable a toggle to hide that option. Disable "Add Money To Merchant" to show the Add Money button as disabled in Manager Merchant Management.</small>
                  </div>
                    </div>
                  </div>
                ) : createUserType === 'manager' ? (
                  <>
                    <div className="form-group">
                      <label htmlFor="name">Full Name *</label>
                      <input
                        type="text"
                        id="name"
                        className="form-control"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Enter full name"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="email">Email Address *</label>
                      <input
                        type="email"
                        id="email"
                        className="form-control"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="Enter email address"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="password">Password *</label>
                      <input
                        type="password"
                        id="password"
                        className="form-control"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        placeholder="Enter password (min 6 characters)"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="phone">Mobile Number *</label>
                      <input
                        type="tel"
                        id="phone"
                        className="form-control"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value.replace(/\D/g, '').slice(0, 15) })}
                        placeholder="Enter 10-digit mobile number"
                        maxLength={15}
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="merchant_id">Merchant ID *</label>
                      <input
                        type="text"
                        id="merchant_id"
                        className="form-control"
                        value={formData.merchant_id}
                        onChange={(e) => setFormData({ ...formData, merchant_id: e.target.value })}
                        placeholder="Enter Merchant ID"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="api_key">API Key *</label>
                      <input
                        type="text"
                        id="api_key"
                        className="form-control"
                        value={formData.api_key}
                        onChange={(e) => setFormData({ ...formData, api_key: e.target.value })}
                        placeholder="Enter API Key"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="api_secret">API Secret *</label>
                      <input
                        type="text"
                        id="api_secret"
                        className="form-control"
                        value={formData.api_secret}
                        onChange={(e) => setFormData({ ...formData, api_secret: e.target.value })}
                        placeholder="Enter API Secret"
                      />
                    </div>
                  </>
                ) : createUserType === 'admin' ? (
                  <>
                    <div className="form-group">
                      <label htmlFor="name">Full Name *</label>
                      <input
                        type="text"
                        id="name"
                        className="form-control"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Enter full name"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="email">Email Address *</label>
                      <input
                        type="email"
                        id="email"
                        className="form-control"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="Enter email address"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="password">Password *</label>
                      <input
                        type="password"
                        id="password"
                        className="form-control"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        placeholder="Enter password (min 6 characters)"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="phone">Mobile Number *</label>
                      <input
                        type="tel"
                        id="phone"
                        className="form-control"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value.replace(/\D/g, '').slice(0, 15) })}
                        placeholder="Enter 10-digit mobile number"
                        maxLength={15}
                      />
                    </div>
                    <div className="form-group">
                      <div className="admin-controls-checkbox-row">
                        <input
                          type="checkbox"
                          id="create-admin-unauthorised-ip-cb"
                          checked={formData.unauthorizedIpDetected}
                          onChange={(e) => setFormData(prev => ({ ...prev, unauthorizedIpDetected: e.target.checked }))}
                        />
                        <label htmlFor="create-admin-unauthorised-ip-cb" className="admin-controls-checkbox-label">
                          UNAUTHORISED IP DETECTED
                        </label>
                      </div>
                      <small className="admin-controls-description">
                        When enabled, this admin must pass IP whitelist checks during login.
                      </small>
                    </div>
                  </>
                ) : null}

              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowCreateModal(false)}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={submitting}
                >
                  {submitting ? 'Creating...' : `Create ${createUserType === 'manager' ? 'Manager' : createUserType === 'admin' ? 'Admin' : 'Merchant'}`}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Toggle Status Modal */}
      {showToggleModal && selectedUser && (
        <div className="modal-overlay" onClick={() => setShowToggleModal(false)}>
          <div className="modal-content1" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{selectedUser.is_active ? 'Deactivate' : 'Activate'} User</h2>
              <button className="modal-close" onClick={() => setShowToggleModal(false)}>✕</button>
            </div>
            <div className="modal-body1">
              <div className="confirmation-content">
                <div className="confirmation-icon">
                  {selectedUser.is_active ? '🔒' : '🔓'}
                </div>
                <div className="confirmation-text">
                  <h3>Are you sure you want to {selectedUser.is_active ? 'deactivate' : 'activate'} this user?</h3>
                  <div className="user-info1">
                    <p><strong>Name:</strong> {selectedUser.name}</p>
                    <p><strong>Email:</strong> {selectedUser.email}</p>
                    <p><strong>Current Status:</strong> 
                      <span className={`status-badge ${selectedUser.is_active ? 'active' : 'inactive'}`}>
                        {selectedUser.is_active ? '✓ Active' : '✗ Inactive'}
                      </span>
                    </p>
                  </div>
                  <p
                    className="confirmation-warning"
                    style={{
                      background: 'rgba(104, 96, 195, 0.95)', 
                      color: 'rgba(98, 61, 0, 0.95)',
                      border: '1px solid rgba(251, 191, 36, 0.55)',
                      borderRadius: 12,
                      padding: '12px 16px'
                    }}
                  >
                    {selectedUser.is_active 
                      ? 'The user will not be able to access their account after deactivation.' 
                      : 'The user will be able to access their account after activation.'}
                  </p>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setShowToggleModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`btn-primary ${selectedUser.is_active ? 'btn-warning' : 'btn-success'}`}
                onClick={confirmToggleStatus}
                disabled={toggleSubmitting}
              >
                {toggleSubmitting ? 'Please wait...' : selectedUser.is_active ? 'Deactivate User' : 'Activate User'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showPasswordModal && selectedUser && (
        <div className="modal-overlay" onClick={() => setShowPasswordModal(false)}>
          <div className="modal-content1" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Reset Password</h2>
              <button className="modal-close" onClick={() => setShowPasswordModal(false)}>✕</button>
            </div>
            <div className="modal-body1">
              <div className="user-info1">
                <p><strong>Name:</strong> {selectedUser.name}</p>
                <p><strong>Email:</strong> {selectedUser.email}</p>
              </div>
              <div className="form-group">
                <label htmlFor="newPassword">New Password *</label>
                <input
                  type="password"
                  id="newPassword"
                  className="form-control"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password (min 6 characters)"
                />
                {newPassword && newPassword.length < 6 && (
                  <div className="error-message">Password must be at least 6 characters long</div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setShowPasswordModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={confirmResetPassword}
                disabled={!newPassword || newPassword.length < 6}
              >
                Reset Password
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete User Modal */}
      {showDeleteModal && selectedUser && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal-content1 danger-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Delete User</h2>
              <button className="modal-close" onClick={() => setShowDeleteModal(false)}>✕</button>
            </div>
            <div className="modal-body1">
              <div className="confirmation-content">
                <div className="confirmation-icon">🗑️</div>
                <div className="confirmation-text">
                  <h3>Are you sure you want to delete this user?</h3>
                  <div className="user-info1">  
                    <p><strong>Name:</strong> {selectedUser.name}</p>
                    <p><strong>Email:</strong> {selectedUser.email}</p>
                    <p><strong>Role:</strong> {selectedUser.role_name}</p>
                  </div>
                  <p
                    className="confirmation-warning"
                    style={{
                      background: 'rgba(82, 85, 177, 0.95)',
                      color: 'rgba(98, 61, 0, 0.95)',
                      border: '1px solid rgba(251, 191, 36, 0.55)',
                      borderRadius: 12,
                      padding: '12px 16px'
                    }}
                  >
                    This action will permanently delete this user account and related access. This cannot be undone.
                  </p>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setShowDeleteModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-danger"
                onClick={confirmDeleteUser}
              >
                Delete User
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal - rendered via portal to escape dark theme inheritance */}
      {showEditModal && selectedUser && createPortal(
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content1" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit {selectedUser.role_name === 'manager' ? 'Manager' : selectedUser.role_name === 'merchant' ? 'Merchant' : 'User'}</h2>
              <button className="modal-close" onClick={() => setShowEditModal(false)}>✕</button>
            </div>
            <div className="modal-body1">
              <div className="form-group">
                <label htmlFor="edit_name">Full Name</label>
                <input
                  type="text"
                  id="edit_name"
                  className="form-control"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  placeholder="Enter full name"
                />
              </div>
              <div className="form-group">
                <label htmlFor="edit_email">Email Address</label>
                <input
                  type="email"
                  id="edit_email"
                  className="form-control"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  placeholder="Enter email address"
                />
              </div>
              <div className="form-group form-group-edit-phone">
                <label htmlFor="edit_phone">Mobile Number *</label>
                <input
                  type="tel"
                  id="edit_phone"
                  className="form-control"
                  value={editForm.phone}
                  onChange={(e) => setEditForm({ ...editForm, phone: e.target.value.replace(/\D/g, '').slice(0, 15) })}
                  placeholder="Enter 10-digit mobile number"
                  maxLength={15}
                />
              </div>

              {/* Payout Options - Show for merchants */}
              {selectedUser.role_name === 'merchant' && (
                <div className="form-group payout-options-section">
                  <label className="payout-options-label">Payout Options</label>
                  <div className="form-group admin-controls-row">
                    <div className="admin-controls-checkbox-row">
                      <input
                        type="checkbox"
                        id="edit-admin-controls-cb"
                        checked={editToggleForm.adminControlsPayoutOptions}
                        onChange={(e) => setEditToggleForm(prev => ({ ...prev, adminControlsPayoutOptions: e.target.checked }))}
                      />
                      <label htmlFor="edit-admin-controls-cb" className="admin-controls-checkbox-label">Admin controls payout options</label>
                    </div>
                    <small className="admin-controls-description">Manager cannot enable/disable these toggles when checked.</small>
                  </div>
                  <div className="payout-toggles-grid">
                    <div className="payout-toggle-row">
                      <span className="payout-toggle-label">Viyona Payout</span>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={editToggleForm.viyonaEnabled}
                        className={`payout-toggle-btn ${editToggleForm.viyonaEnabled ? 'on' : 'off'}`}
                        onClick={() => setEditToggleForm(prev => ({ ...prev, viyonaEnabled: !prev.viyonaEnabled }))}
                      >
                        <span className="payout-toggle-knob" />
                      </button>
                    </div>
                    <div className="payout-toggle-row">
                      <span className="payout-toggle-label">PoolPay Payout</span>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={editToggleForm.poolpayEnabled}
                        className={`payout-toggle-btn ${editToggleForm.poolpayEnabled ? 'on' : 'off'}`}
                        onClick={() => setEditToggleForm(prev => ({ ...prev, poolpayEnabled: !prev.poolpayEnabled }))}
                      >
                        <span className="payout-toggle-knob" />
                      </button>
                    </div>
                    <div className="payout-toggle-row">
                      <span className="payout-toggle-label">Add Money To Merchant</span>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={editToggleForm.addMoneyEnabled}
                        className={`payout-toggle-btn ${editToggleForm.addMoneyEnabled ? 'on' : 'off'}`}
                        onClick={() => setEditToggleForm(prev => ({ ...prev, addMoneyEnabled: !prev.addMoneyEnabled }))}
                      >
                        <span className="payout-toggle-knob" />
                      </button>
                    </div>
                  </div>
                  <small className="payout-options-hint">Disable a toggle to hide that option. Disable &quot;Add Money To Merchant&quot; to grey out the Add Money button in Manager Merchant Management.</small>
                </div>
              )}

              {/* API Fields - Show for managers */}
              {selectedUser.role_name === 'manager' && (
                <>
                  <div className="form-group">
                    <label htmlFor="edit_client_id">Client ID</label>
                    <input
                      type="text"
                      id="edit_client_id"
                      className="form-control"
                      value={editApiForm.client_id}
                      onChange={(e) => setEditApiForm({ ...editApiForm, client_id: e.target.value })}
                      placeholder="Enter client ID"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="edit_client_secret">Client Secret</label>
                    <input
                      type="text"
                      id="edit_client_secret"
                      className="form-control"
                      value={editApiForm.client_secret}
                      onChange={(e) => setEditApiForm({ ...editApiForm, client_secret: e.target.value })}
                      placeholder="Enter client secret"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="edit_api_key">API Key</label>
                    <input
                      type="text"
                      id="edit_api_key"
                      className="form-control"
                      value={editApiForm.api_key}
                      onChange={(e) => setEditApiForm({ ...editApiForm, api_key: e.target.value })}
                      placeholder="Enter API key"
                    />
                  </div>
                </>
              )}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setShowEditModal(false)}
                disabled={editSubmitting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={async () => {
                  if (!selectedUser) return;
                  try {
                    setEditSubmitting(true);
                    const updateData: any = selectedUser.role_name === 'admin'
                      ? { name: editForm.name, email: editForm.email, phone: editForm.phone || undefined }
                      : {
                          name: editForm.name,
                          email: editForm.email,
                          phone: editForm.phone || undefined
                        };
                    // Add API fields for managers
                    if (selectedUser.role_name === 'manager') {
                      updateData.client_id = editApiForm.client_id;
                      updateData.api_key = editApiForm.api_key;
                      updateData.client_secret = editApiForm.client_secret;
                    }
                    // Add payout toggles and admin control flag for merchants
                    if (selectedUser.role_name === 'merchant') {
                      updateData.viyona_enabled = editToggleForm.viyonaEnabled;
                      updateData.poolpay_enabled = editToggleForm.poolpayEnabled;
                      updateData.add_money_enabled = editToggleForm.addMoneyEnabled;
                      updateData.admin_controls_payout_options = editToggleForm.adminControlsPayoutOptions;
                    }
                    await adminAPI.updateUser(selectedUser.id, updateData);
                    setShowEditModal(false);
                    setNotificationMessage(
                      `${selectedUser.role_name === 'admin' ? 'Admin email' : selectedUser.role_name === 'manager' ? 'Manager' : 'Merchant'} updated successfully!`
                    );
                    setNotificationType('success');
                    setShowNotificationModal(true);
                    // refresh current tab
                    if (activeTab === 'managers') {
                      fetchManagers();
                    } else if (activeTab === 'admins') {
                      fetchAdmins();
                    } else {
                      fetchMerchants();
                    }
                  } catch (e) {
                    console.error('Error updating user', e);
                    setNotificationMessage('Failed to update user');
                    setNotificationType('error');
                    setShowNotificationModal(true);
                  } finally {
                    setEditSubmitting(false);
                  }
                }}
                disabled={editSubmitting}
              >
                {editSubmitting ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Merchants by Manager Modal */}
      {showMerchantsModal && (
        <div className="modal-overlay" onClick={() => setShowMerchantsModal(false)}>
          <div className="modal-content1 modal-wide" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Merchants created by {selectedManagerForMerchants?.name}</h2>
              <button className="modal-close" onClick={() => setShowMerchantsModal(false)}>✕</button>
            </div>
            <div className="modal-body1">
              {loadingManagerMerchants ? (
                <div className="loading-container">
                  <div className="loading-spinner"></div>
                  <p>Loading merchants...</p>
                </div>
              ) : managerMerchants.length === 0 ? (
                <div className="empty-state">
                  <span className="empty-icon">📭</span>
                  <h3>No merchants found</h3>
                  <p>This manager hasn't created any merchants yet.</p>
                </div>
              ) : (
                <div className="table-container">
                  <table className="transaction-table merchants-table">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>NAME</th>
                        <th>EMAIL</th>
                        <th>WALLET BALANCE</th>
                        <th>ACCOUNT BALANCE</th>
                        <th>PAYOUTS</th>
                        <th>STATUS</th>
                        <th>CREATED</th>
                      </tr>
                    </thead>
                    <tbody>
                      {managerMerchants.map((m) => (
                        <tr key={m.id} className={!m.is_active ? 'inactive-row' : ''}>
                          <td>{m.id}</td>
                          <td>
                            <div className="user-info1">
                              <strong>{m.name}</strong>
                              <span className="user-email">{m.email}</span>
                            </div>
                          </td>
                          <td>{m.email}</td>
                          <td className="currency">{formatCurrency(m.wallet_balance)}</td>
                          <td className="currency">{formatCurrency(m.account_balance || 0)}</td>
                          <td className="center">{m.total_payouts || 0}</td>
                          <td>
                            <span className={`status-badge ${m.is_active ? 'active' : 'inactive'}`}>
                              {m.is_active ? '✓ Active' : '✗ Inactive'}
                            </span>
                          </td>
                          <td className="date">{formatDate(m.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowMerchantsModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Blacklist Confirmation Modal */}
      {showBannedConfirmModal && bannedTargetUser && createPortal(
        <div
          className="modal-overlay"
          onClick={() => {
            setShowBannedConfirmModal(false);
            setBannedTargetUser(null);
            setBannedReason('');
          }}
        >
          <div className="modal-content1" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add To Banned Merchants</h2>
              <button
                className="modal-close"
                onClick={() => {
                  setShowBannedConfirmModal(false);
                  setBannedTargetUser(null);
                  setBannedReason('');
                }}
              >
                ✕
              </button>
            </div>
            <div className="modal-body1">
              <div className="confirmation-content">
                <div className="confirmation-icon">🚫</div>
                <div className="confirmation-text">
                  <h3>Are you sure you want to add this merchant to Banned Merchants?</h3>
                  <div className="user-info1">
                    <p><strong>Name:</strong> {bannedTargetUser.name}</p>
                    <p><strong>Email:</strong> {bannedTargetUser.email}</p>
                    <p><strong>Role:</strong> {bannedTargetUser.role_name}</p>
                  </div>

                  <div className="form-group" style={{ marginTop: 14 }}>
                    <label htmlFor="banned_reason">Banned Reason (optional)</label>
                    <textarea
                      id="banned_reason"
                      className="form-control"
                      value={bannedReason}
                      onChange={(e) => setBannedReason(e.target.value)}
                      placeholder="Enter reason for banning this merchant"
                      rows={3}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => {
                  setShowBannedConfirmModal(false);
                  setBannedTargetUser(null);
                  setBannedReason('');
                }}
              >
                Cancel
              </button>
              <button type="button" className="btn-primary" onClick={confirmAddToBanned}>
                Confirm Ban
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Blacklist Confirmation Modal */}
      {showBlacklistConfirmModal && blacklistTargetUser && createPortal(
        <div
          className="modal-overlay"
          onClick={() => {
            setShowBlacklistConfirmModal(false);
            setBlacklistTargetUser(null);
            setBlacklistReason('');
            setBlacklistReasonError('');
          }}
        >
          <div className="modal-content1" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add To Blacklisted Merchants</h2>
              <button
                className="modal-close"
                onClick={() => {
                  setShowBlacklistConfirmModal(false);
                  setBlacklistTargetUser(null);
                  setBlacklistReason('');
                  setBlacklistReasonError('');
                }}
              >
                ✕
              </button>
            </div>
            <div className="modal-body1">
              <div className="confirmation-content">
                <div className="confirmation-icon">⛔</div>
                <div className="confirmation-text">
                  <h3>Are you sure you want to add this merchant to Blacklisted Merchants?</h3>
                  <div className="user-info1">
                    <p><strong>Name:</strong> {blacklistTargetUser.name}</p>
                    <p><strong>Email:</strong> {blacklistTargetUser.email}</p>
                    <p><strong>Role:</strong> {blacklistTargetUser.role_name}</p>
                  </div>

                  <div className="form-group" style={{ marginTop: 14 }}>
                    <label htmlFor="blacklist_reason">Blacklisted Reason *</label>
                    <textarea
                      id="blacklist_reason"
                      className="form-control"
                      value={blacklistReason}
                      onChange={(e) => {
                        setBlacklistReason(e.target.value);
                        if (blacklistReasonError) setBlacklistReasonError('');
                      }}
                      placeholder="Enter reason for blacklisting this merchant"
                      rows={3}
                    />
                    {blacklistReasonError && (
                      <div className="error-message" style={{ marginTop: 8 }}>{blacklistReasonError}</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => {
                  setShowBlacklistConfirmModal(false);
                  setBlacklistTargetUser(null);
                  setBlacklistReason('');
                  setBlacklistReasonError('');
                }}
              >
                Cancel
              </button>
              <button type="button" className="btn-primary" onClick={confirmAddToBlacklisted}>
                Confirm Blacklist
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Notification Modal (replaces browser alert) */}
      {showNotificationModal && createPortal(
        <div className="modal-overlay" onClick={() => setShowNotificationModal(false)}>
          <div className={`modal-content1 notification-modal ${notificationType === 'success' ? 'success' : 'notification-error'}`} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{notificationType === 'success' ? '✓ Success' : '⚠ Error'}</h2>
              <button className="modal-close" onClick={() => setShowNotificationModal(false)}>✕</button>
            </div>
            <div className="modal-body1">
              <p className="notification-message">{notificationMessage}</p>
            </div>
            <div className="modal-footer">
              <button className="btn-primary" onClick={() => setShowNotificationModal(false)}>OK</button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default UserManagement;

